/**
 * Stage Configuration System
 * 
 * This file defines the detailed stages (for internal tracking) and simplified
 * stepper stages (for display), along with their mappings and sub-statuses.
 * 
 * NEW: Dynamic Stage Workflow System
 * - Fixed mandatory stages (always visible)
 * - Optional conditional stages (dynamic visibility)
 * - Dependency-based completion logic
 * - Action rules for next action buttons
 * 
 * NOTE: The stage master is now stored in the database (lead_stage_master table).
 * The hardcoded config below serves as a fallback and for type reference.
 * Use the useLeadStages() hook for dynamic stage data.
 */

// DetailedStage is now a string to support dynamically-added stages from DB.
// Known stages below are kept as reference constants.
export type DetailedStage = string;

// Map stages to numbers for database optimization
export const STAGE_ID_MAP: Record<DetailedStage, number> = {
  new: 1,
  contacted: 2,
  followup: 3,
  storeVisit: 4,
  designFinalized: 5,
  siteVisit: 6,
  measurement: 7,
  quotation: 8,
  won: 9,
  lost: 10,
  hold: 11,
};

// Stage status types for state machine
export type StageStatus = 'pending' | 'completed' | 'skipped' | 'hidden';

// Stage interface for state management
export interface Stage {
  key: DetailedStage;
  label: string;
  visible: boolean;
  status: StageStatus;
  completedAt?: string;
}

// Master stage configuration - defines stage types
export interface StageConfig {
  key: DetailedStage;
  fixed?: boolean;      // Fixed mandatory stage (always visible)
  optional?: boolean;    // Optional conditional stage (dynamic visibility)
  terminal?: boolean;    // Terminal stage (won/lost/hold)
  // Note: For optional stages (storeVisit, designFinalized, siteVisit),
  // the order is determined by stageSelectedAt timestamp in Lead data,
  // not by position in this array. First selected stage appears first.
}

// Master stage configuration
export const STAGE_CONFIG: StageConfig[] = [
  { key: 'new', fixed: true },
  { key: 'contacted', fixed: true },
  { key: 'followup', fixed: true },
  { key: 'storeVisit', optional: true },
  { key: 'designFinalized', optional: true },
  { key: 'siteVisit', optional: true },
  { key: 'measurement', fixed: true },
  { key: 'quotation', fixed: true },
  { key: 'won', terminal: true },
  { key: 'lost', terminal: true },
  { key: 'hold', terminal: true },
];

// Dependency map - defines which stages must be completed before another
export const DEPENDENCY_MAP: Record<DetailedStage, DetailedStage[]> = {
  new: [],
  contacted: ['new'],
  followup: ['contacted'],
  storeVisit: ['followup'], // Optional, but if done, requires followup
  designFinalized: ['followup'], // Optional, but if done, requires followup
  siteVisit: ['followup'], // Optional, but if done, requires followup
  measurement: ['followup'], // Requires followup (not necessarily site/store/design)
  quotation: ['measurement'],
  won: ['quotation'],
  lost: ['quotation'],
  hold: ['quotation'],
};

// Next action types
export type NextAction =
  | 'callLater'
  | 'notInterested'
  | 'storeVisit'
  | 'storeVisitCompleted'
  | 'siteVisit'
  | 'siteVisitCompleted'
  | 'designFinalized'
  | 'measurementDone'
  | 'quotationSent'
  | 'quotationApproved'
  | 'won'
  | 'lost'
  | 'hold';

// Action rules - defines when each action is allowed
export interface ActionRule {
  allowedAfter: DetailedStage[]; // Stages that must be completed (at least one)
  requiresAll?: boolean; // If true, all stages in allowedAfter must be completed
}

export const ACTION_RULES: Record<NextAction, ActionRule> = {
  callLater: {
    allowedAfter: ['new', 'contacted', 'followup'], // Can call later from any stage
  },
  notInterested: {
    allowedAfter: ['new', 'contacted', 'followup'], // Can mark not interested from any stage
  },
  storeVisit: {
    allowedAfter: ['new', 'contacted', 'followup'], // Can schedule store visit from early stages
  },
  storeVisitCompleted: {
    allowedAfter: ['storeVisit'],
  },
  siteVisit: {
    allowedAfter: ['new', 'contacted', 'followup'], // Can schedule site visit from early stages
  },
  siteVisitCompleted: {
    allowedAfter: ['siteVisit'],
  },
  designFinalized: {
    allowedAfter: ['followup', 'storeVisit', 'siteVisit'], // Can be done after followup or visits
  },
  measurementDone: {
    allowedAfter: ['followup'], // Can be done after followup (doesn't require site/store/design)
  },
  quotationSent: {
    allowedAfter: ['measurement'],
  },
  quotationApproved: {
    allowedAfter: ['quotation'],
  },
  won: {
    allowedAfter: ['quotation'],
  },
  lost: {
    allowedAfter: ['quotation'],
  },
  hold: {
    allowedAfter: ['quotation'],
  },
};

// Sub-statuses for stages that support them
export type StoreVisitSubStatus = 'scheduled' | 'completed';
export type SiteVisitSubStatus = 'scheduled' | 'completed';
export type QuotationSubStatus = 'send' | 'approved';

// Simplified stepper stages for display
export type StepperStage =
  | 'enquiry'
  | 'contacted'
  | 'followup'
  | 'storeVisit'
  | 'designFinalized'
  | 'siteVisit'
  | 'measurement'
  | 'quotation'
  | 'wonLostHold';

// Stage configuration interface
export interface DetailedStageConfig {
  key: DetailedStage;
  label: string;
  description: string;
  stepperStage: StepperStage;
  stageId: number;
  supportsSubStatus?: boolean;
  subStatuses?: StoreVisitSubStatus[] | SiteVisitSubStatus[] | QuotationSubStatus[];
}

// Stepper stage configuration
export interface StepperStageConfig {
  key: StepperStage;
  label: string;
  displayLabel: string;
  supportsSubStatus: boolean;
  subStatusColors?: {
    [key: string]: {
      color: string;
      bgColor: string;
      label: string;
    };
  };
}

// Detailed stages configuration
export const DETAILED_STAGES: DetailedStageConfig[] = [
  {
    key: 'new',
    label: 'New',
    description: 'Newly created lead',
    stepperStage: 'enquiry',
    stageId: 1,
  },
  {
    key: 'contacted',
    label: 'Contacted',
    description: 'When we can connect to a contact/lead through phone, email, or WhatsApp',
    stepperStage: 'contacted',
    stageId: 2,
  },
  {
    key: 'followup',
    label: 'Follow Up',
    description: 'How we connected to a customer',
    stepperStage: 'followup',
    stageId: 3,
  },
  {
    key: 'storeVisit',
    label: 'Store Visit',
    description: 'Visit to store (not mandatory - can select design using WhatsApp also)',
    stepperStage: 'storeVisit',
    stageId: 4,
    supportsSubStatus: true,
    subStatuses: ['scheduled', 'completed'],
  },
  {
    key: 'designFinalized',
    label: 'Design Finalized',
    description: 'Design has been finalized (can be done via WhatsApp or store visit)',
    stepperStage: 'designFinalized',
    stageId: 5,
    supportsSubStatus: false,
  },
  {
    key: 'siteVisit',
    label: 'Site Visit',
    description: 'First visit store then visit site (not mandatory - depends on user)',
    stepperStage: 'siteVisit',
    stageId: 6,
    supportsSubStatus: true,
    subStatuses: ['scheduled', 'completed'],
  },
  {
    key: 'measurement',
    label: 'Measurement',
    description: 'Worker goes to site visit and takes measurement',
    stepperStage: 'measurement',
    stageId: 7,
  },
  {
    key: 'quotation',
    label: 'Quotation',
    description: 'Send/approved quotation',
    stepperStage: 'quotation',
    stageId: 8,
    supportsSubStatus: true,
    subStatuses: ['send', 'approved'],
  },
  {
    key: 'won',
    label: 'Won',
    description: 'Lead converted to order',
    stepperStage: 'wonLostHold',
    stageId: 9,
  },
  {
    key: 'lost',
    label: 'Lost',
    description: 'Lead lost',
    stepperStage: 'wonLostHold',
    stageId: 10,
  },
  {
    key: 'hold',
    label: 'Hold',
    description: 'Lead on hold',
    stepperStage: 'wonLostHold',
    stageId: 11,
  },
];

// Stepper stages configuration (simplified view)
export const STEPPER_STAGES: StepperStageConfig[] = [
  {
    key: 'enquiry',
    label: 'Enquiry',
    displayLabel: 'Enquiry (New)',
    supportsSubStatus: false,
  },
  {
    key: 'contacted',
    label: 'Contacted',
    displayLabel: 'Contacted',
    supportsSubStatus: false,
  },
  {
    key: 'followup',
    label: 'Follow Up',
    displayLabel: 'Follow Up',
    supportsSubStatus: false,
  },
  {
    key: 'storeVisit',
    label: 'Store Visit',
    displayLabel: 'Store Visit',
    supportsSubStatus: true,
    subStatusColors: {
      scheduled: {
        color: '#FF9800', // Orange
        bgColor: '#FFF3E0',
        label: 'Scheduled',
      },
      completed: {
        color: '#4CAF50', // Green
        bgColor: '#E8F5E9',
        label: 'Completed',
      },
    },
  },
  {
    key: 'designFinalized',
    label: 'Design Finalized',
    displayLabel: 'Design Finalized',
    supportsSubStatus: false,
  },
  {
    key: 'siteVisit',
    label: 'Site Visit',
    displayLabel: 'Site Visit',
    supportsSubStatus: true,
    subStatusColors: {
      scheduled: {
        color: '#FF9800', // Orange
        bgColor: '#FFF3E0',
        label: 'Scheduled',
      },
      completed: {
        color: '#4CAF50', // Green
        bgColor: '#E8F5E9',
        label: 'Completed',
      },
    },
  },
  {
    key: 'measurement',
    label: 'Measurement',
    displayLabel: 'Measurement',
    supportsSubStatus: false,
  },
  {
    key: 'quotation',
    label: 'Quotation',
    displayLabel: 'Quotation',
    supportsSubStatus: true,
    subStatusColors: {
      send: {
        color: '#FF9800', // Orange
        bgColor: '#FFF3E0',
        label: 'Send',
      },
      approved: {
        color: '#4CAF50', // Green
        bgColor: '#E8F5E9',
        label: 'Approved',
      },
    },
  },
  {
    key: 'wonLostHold',
    label: 'Won/Lost/Hold',
    displayLabel: 'Won/Lost/Hold',
    supportsSubStatus: false,
  },
];

// Helper functions
export const getDetailedStageByKey = (key: DetailedStage): DetailedStageConfig | undefined => {
  return DETAILED_STAGES.find(stage => stage.key === key);
};

export const getStepperStageByKey = (key: StepperStage): StepperStageConfig | undefined => {
  return STEPPER_STAGES.find(stage => stage.key === key);
};

export const getStageId = (detailedStage: DetailedStage): number => {
  return STAGE_ID_MAP[detailedStage] || 1;
};

export const getStageKeyFromId = (stageId: number): DetailedStage => {
  const entry = Object.entries(STAGE_ID_MAP).find(([_, id]) => id === stageId);
  return (entry ? entry[0] : 'new') as DetailedStage;
};

export const getStepperStageFromDetailed = (detailedStage: DetailedStage): StepperStage => {
  const config = getDetailedStageByKey(detailedStage);
  return config?.stepperStage || 'enquiry';
};

export const getDetailedStagesForStepper = (stepperStage: StepperStage): DetailedStageConfig[] => {
  return DETAILED_STAGES.filter(stage => stage.stepperStage === stepperStage);
};

// Get stepper stage index for display
export const getStepperStageIndex = (stepperStage: StepperStage): number => {
  return STEPPER_STAGES.findIndex(stage => stage.key === stepperStage);
};

// Get sub-status color configuration
export const getSubStatusColor = (
  stepperStage: StepperStage,
  subStatus: string
): { color: string; bgColor: string; label: string } | null => {
  const config = getStepperStageByKey(stepperStage);
  if (!config?.subStatusColors) return null;
  return config.subStatusColors[subStatus] || null;
};

// Validate stage transition
export const isValidStageTransition = (
  currentStage: DetailedStage,
  newStage: DetailedStage
): boolean => {
  const currentIndex = DETAILED_STAGES.findIndex(s => s.key === currentStage);
  const newIndex = DETAILED_STAGES.findIndex(s => s.key === newStage);

  // Allow moving forward, backward, or staying at same level
  // You can add more complex logic here if needed
  return true;
};

// Type for follow-up activity (simplified for ordering logic)
type FollowUpActivityForOrdering = {
  type?: 'phone' | 'whatsapp' | 'email' | 'siteVisit' | 'storeVisit' | 'followup';
  channel?: string;
  date: string;
};

// Get dynamic order of flexible stages (storeVisit, designFinalized, siteVisit)
// based on which one was selected first (using stageSelectedAt timestamps)
// Falls back to follow-up history if timestamps are not available
export const getDynamicFlexibleStagesOrder = (
  followUpHistory?: FollowUpActivityForOrdering[],
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string,
  stageSelectedAt?: Partial<Record<'storeVisit' | 'designFinalized' | 'siteVisit', string>>
): StepperStage[] => {
  // The three flexible stages that can be reordered
  const flexibleStages: StepperStage[] = ['storeVisit', 'designFinalized', 'siteVisit'];

  // Default order if no history or all stages are incomplete
  const defaultOrder: StepperStage[] = ['storeVisit', 'designFinalized', 'siteVisit'];

  // PRIORITY 1: Use stageSelectedAt timestamps if available (most accurate)
  // This ensures stages maintain their position based on when they were first selected
  if (stageSelectedAt && Object.keys(stageSelectedAt).length > 0) {
    const orderedStages = [...flexibleStages].sort((a, b) => {
      const aTimestamp = stageSelectedAt[a as 'storeVisit' | 'designFinalized' | 'siteVisit'];
      const bTimestamp = stageSelectedAt[b as 'storeVisit' | 'designFinalized' | 'siteVisit'];

      // If both have timestamps, sort by timestamp (earliest first)
      if (aTimestamp && bTimestamp) {
        return new Date(aTimestamp).getTime() - new Date(bTimestamp).getTime();
      }

      // If only one has timestamp, it comes first
      if (aTimestamp && !bTimestamp) return -1;
      if (!aTimestamp && bTimestamp) return 1;

      // If neither has timestamp, maintain default order
      const aDefaultIndex = defaultOrder.indexOf(a);
      const bDefaultIndex = defaultOrder.indexOf(b);
      return aDefaultIndex - bDefaultIndex;
    });

    return orderedStages;
  }

  // PRIORITY 2: Fall back to follow-up history analysis
  // Track when each stage was first reached (not necessarily completed)
  const stageFirstOccurrence: { [key in StepperStage]?: { date: string; index: number } } = {};

  // Analyze follow-up history to find when each stage was first mentioned/reached
  if (followUpHistory && followUpHistory.length > 0) {
    followUpHistory.forEach((activity, index) => {
      const activityDate = activity.date;
      const channel = (activity.channel || '').toLowerCase();

      // Check for storeVisit - any activity with type 'storeVisit' or channel mentioning store visit
      if (activity.type === 'storeVisit' && !stageFirstOccurrence.storeVisit) {
        stageFirstOccurrence.storeVisit = { date: activityDate, index };
      } else if (!stageFirstOccurrence.storeVisit &&
        (channel.includes('store visit') || channel.includes('storevisit'))) {
        stageFirstOccurrence.storeVisit = { date: activityDate, index };
      }

      // Check for siteVisit - any activity with type 'siteVisit' or channel mentioning site visit
      if (activity.type === 'siteVisit' && !stageFirstOccurrence.siteVisit) {
        stageFirstOccurrence.siteVisit = { date: activityDate, index };
      } else if (!stageFirstOccurrence.siteVisit &&
        (channel.includes('site visit') || channel.includes('sitevisit'))) {
        stageFirstOccurrence.siteVisit = { date: activityDate, index };
      }

      // Check for designFinalized - channel mentioning design finalized
      if (!stageFirstOccurrence.designFinalized &&
        (channel.includes('design') && (channel.includes('finalized') || channel.includes('final')))) {
        stageFirstOccurrence.designFinalized = { date: activityDate, index };
      }
    });
  }

  // Also check current stage - if it's one of the flexible stages, it should be prioritized
  if (currentDetailedStage) {
    const currentStepperStage = getStepperStageFromDetailed(currentDetailedStage);
    if (flexibleStages.includes(currentStepperStage)) {
      // If current stage is one of the flexible stages and it's completed or has no sub-status,
      // it should appear first in the order
      const isCompleted =
        !currentStageSubStatus ||
        currentStageSubStatus === 'completed' ||
        currentStageSubStatus === 'approved' ||
        currentStepperStage === 'designFinalized';

      // If completed and no earlier occurrence found, use current as the first occurrence
      if (isCompleted && !stageFirstOccurrence[currentStepperStage]) {
        const latestDate = followUpHistory && followUpHistory.length > 0
          ? followUpHistory[0].date
          : new Date().toISOString();
        stageFirstOccurrence[currentStepperStage] = {
          date: latestDate,
          index: followUpHistory ? followUpHistory.length : 0
        };
      }
    }
  }

  // Sort flexible stages by first occurrence date (earliest first)
  // Stages that have been reached/completed appear first
  const orderedStages = [...flexibleStages].sort((a, b) => {
    const aOccurrence = stageFirstOccurrence[a];
    const bOccurrence = stageFirstOccurrence[b];

    // If both have occurrences, sort by date (earliest first)
    if (aOccurrence && bOccurrence) {
      return new Date(aOccurrence.date).getTime() - new Date(bOccurrence.date).getTime();
    }

    // If only one has occurrence, it comes first
    if (aOccurrence && !bOccurrence) return -1;
    if (!aOccurrence && bOccurrence) return 1;

    // If current stage is one of them and completed, prioritize it
    if (currentDetailedStage) {
      const currentStepperStage = getStepperStageFromDetailed(currentDetailedStage);
      const isCompleted =
        !currentStageSubStatus ||
        currentStageSubStatus === 'completed' ||
        currentStageSubStatus === 'approved' ||
        currentStepperStage === 'designFinalized';

      if (isCompleted) {
        if (a === currentStepperStage && b !== currentStepperStage) return -1;
        if (b === currentStepperStage && a !== currentStepperStage) return 1;
      }
    }

    // If neither has occurrence, maintain default order
    const aDefaultIndex = defaultOrder.indexOf(a);
    const bDefaultIndex = defaultOrder.indexOf(b);
    return aDefaultIndex - bDefaultIndex;
  });

  return orderedStages;
};

// Get dynamically ordered stepper stages based on lead's history
export const getDynamicStepperStages = (
  followUpHistory?: FollowUpActivityForOrdering[],
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string,
  stageSelectedAt?: Partial<Record<'storeVisit' | 'designFinalized' | 'siteVisit', string>>
): StepperStageConfig[] => {
  // Check if current stage is terminal (lost, won, or hold)
  const isTerminal = currentDetailedStage ? isTerminalStage(currentDetailedStage) : false;

  // Fixed stages at the beginning
  const fixedStartStages: StepperStage[] = ['enquiry', 'contacted', 'followup'];

  // Get dynamic order of flexible stages
  let flexibleStages = getDynamicFlexibleStagesOrder(
    followUpHistory,
    currentDetailedStage,
    currentStageSubStatus,
    stageSelectedAt
  );

  // When terminal, filter out flexible stages that weren't actually completed/selected
  // Only show flexible stages that have evidence in stageSelectedAt or followUpHistory
  if (isTerminal) {
    flexibleStages = flexibleStages.filter(stage => {
      // Check if stage was selected (has timestamp in stageSelectedAt)
      if (stageSelectedAt && (stage === 'storeVisit' || stage === 'designFinalized' || stage === 'siteVisit')) {
        if (stageSelectedAt[stage as 'storeVisit' | 'designFinalized' | 'siteVisit']) {
          return true;
        }
      }
      
      // Check follow-up history for evidence of this stage
      if (followUpHistory && followUpHistory.length > 0) {
        for (const activity of followUpHistory) {
          const channel = (activity.channel || '').toLowerCase();
          if (stage === 'storeVisit' && (activity.type === 'storeVisit' || channel.includes('store visit'))) {
            return true;
          }
          if (stage === 'siteVisit' && (activity.type === 'siteVisit' || channel.includes('site visit'))) {
            return true;
          }
          if (stage === 'designFinalized' && channel.includes('design') && (channel.includes('finalized') || channel.includes('final'))) {
            return true;
          }
        }
      }
      
      return false; // Hide flexible stages that weren't completed when terminal
    });
  }

  // Helper functions to check if measurement and quotation were completed or are current stage
  const isMeasurementVisited = (): boolean => {
    // If current stage is measurement, it's visited
    if (currentDetailedStage === 'measurement') {
      return true;
    }
    // Check follow-up history for evidence of measurement completion
    if (followUpHistory && followUpHistory.length > 0) {
      for (const activity of followUpHistory) {
        const channel = (activity.channel || '').toLowerCase();
        // Check if channel mentions "Measurement Done" or "Measurement Completed"
        if (channel.includes('measurement') && (channel.includes('done') || channel.includes('completed'))) {
          return true;
        }
      }
    }
    return false;
  };

  const isQuotationVisited = (): boolean => {
    // If current stage is quotation, it's visited
    if (currentDetailedStage === 'quotation') {
      return true;
    }
    // Check follow-up history for evidence of quotation completion
    if (followUpHistory && followUpHistory.length > 0) {
      for (const activity of followUpHistory) {
        const channel = (activity.channel || '').toLowerCase();
        // Check if channel mentions "Quotation Sent" or "Quotation Approved"
        if (channel.includes('quotation') && (channel.includes('sent') || channel.includes('approved'))) {
          return true;
        }
      }
    }
    return false;
  };

  // Build fixed end stages based on completion evidence
  // If terminal stage, only show measurement/quotation if they were actually visited/completed
  // This handles the case where lead goes directly to lost/won/hold without measurement/quotation
  // (e.g., "not interested" -> lost, skipping measurement and quotation)
  const fixedEndStages: StepperStage[] = [];
  
  if (isTerminal) {
    // When terminal, only include measurement/quotation if they were visited/completed
    // This ensures that if lead goes: new -> contacted -> followup -> storeVisit -> lost
    // The stepper shows: new -> contacted -> followup -> storeVisit -> lost
    // (without measurement and quotation)
    if (isMeasurementVisited()) {
      fixedEndStages.push('measurement');
    }
    if (isQuotationVisited()) {
      fixedEndStages.push('quotation');
    }
    // Always include terminal stage
    fixedEndStages.push('wonLostHold');
  } else {
    // When not terminal, always show measurement and quotation (they may be pending or current)
    fixedEndStages.push('measurement', 'quotation', 'wonLostHold');
  }

  // Combine all stages in order
  const orderedStepperStages: StepperStage[] = [
    ...fixedStartStages,
    ...flexibleStages,
    ...fixedEndStages,
  ];

  // Map to StepperStageConfig objects
  return orderedStepperStages
    .map(stageKey => getStepperStageByKey(stageKey))
    .filter((config): config is StepperStageConfig => config !== undefined);
};

// Get stepper stage index for display (works with dynamic ordering)
export const getStepperStageIndexDynamic = (
  stepperStage: StepperStage,
  orderedStages: StepperStageConfig[]
): number => {
  return orderedStages.findIndex(stage => stage.key === stepperStage);
};

// Check if a flexible stage (storeVisit, designFinalized, siteVisit) has been completed
// based on follow-up history and current stage
export const isFlexibleStageCompleted = (
  stage: 'storeVisit' | 'designFinalized' | 'siteVisit',
  followUpHistory?: FollowUpActivityForOrdering[],
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string
): boolean => {
  // If current stage is the one we're checking
  if (currentDetailedStage === stage) {
    if (stage === 'designFinalized') {
      // designFinalized is always considered completed when it's the current stage
      return true;
    }
    // For storeVisit and siteVisit, check sub-status
    // If sub-status is 'completed', the stage is done
    return currentStageSubStatus === 'completed';
  }

  // If current stage is one of the flexible stages but different from what we're checking
  // and it's completed, we can infer the checked stage might not be completed yet
  // But we need to check history to be sure

  // Check follow-up history for completion indicators
  if (followUpHistory && followUpHistory.length > 0) {
    for (const activity of followUpHistory) {
      const channel = (activity.channel || '').toLowerCase();

      if (stage === 'storeVisit') {
        // Check if store visit was completed
        if (activity.type === 'storeVisit') {
          // If channel mentions completed or we see a "Mark Store Visit Completed" action
          if (channel.includes('completed') || channel.includes('store visit completed')) {
            return true;
          }
        }
      } else if (stage === 'siteVisit') {
        // Check if site visit was completed
        if (activity.type === 'siteVisit') {
          if (channel.includes('completed') || channel.includes('site visit completed')) {
            return true;
          }
        }
      } else if (stage === 'designFinalized') {
        // Check if design was finalized
        if (channel.includes('design') && (channel.includes('finalized') || channel.includes('final'))) {
          return true;
        }
      }
    }
  }

  // If current stage is beyond the flexible stages (measurement, quotation, etc.)
  // and we haven't found completion evidence, assume it's not completed
  // This allows users to go back and complete missed stages
  if (currentDetailedStage) {
    const laterStages: DetailedStage[] = ['measurement', 'quotation', 'won', 'lost', 'hold'];
    if (laterStages.includes(currentDetailedStage)) {
      // We're at a later stage, but if we didn't find completion evidence,
      // the stage is not completed
      return false;
    }
  }

  return false;
};

// ============================================================================
// NEW: Dynamic Stage Workflow Utilities
// ============================================================================

/**
 * Get stage visibility based on follow-up history and current stage
 * A stage is visible if:
 * - It's a fixed stage (always visible)
 * - It's an optional stage that has been triggered (appears in history or stageSelectedAt)
 */
export const getStageVisibility = (
  stageKey: DetailedStage,
  followUpHistory?: Array<{
    type?: 'phone' | 'whatsapp' | 'email' | 'siteVisit' | 'storeVisit' | 'followup';
    channel?: string;
    date: string;
  }>,
  currentDetailedStage?: DetailedStage,
  stageSelectedAt?: Partial<Record<'storeVisit' | 'designFinalized' | 'siteVisit', string>>
): boolean => {
  const config = STAGE_CONFIG.find(s => s.key === stageKey);

  // Fixed stages are always visible
  if (config?.fixed) {
    return true;
  }

  // Terminal stages are always visible
  if (config?.terminal) {
    return true;
  }

  // Optional stages are visible only if triggered
  // Important: Optional stages can be SKIPPED, so we should NOT assume they were visited
  // just because the current stage is beyond them in the fixed order.
  if (config?.optional) {
    // Check if current stage is this stage
    if (currentDetailedStage === stageKey) {
      return true;
    }

    // Check if stage was selected (has timestamp in stageSelectedAt)
    // This is the PRIMARY source of truth for optional stage visibility
    if (stageSelectedAt && (stageKey === 'storeVisit' || stageKey === 'designFinalized' || stageKey === 'siteVisit')) {
      if (stageSelectedAt[stageKey as 'storeVisit' | 'designFinalized' | 'siteVisit']) {
        return true;
      }
    }

    // Check follow-up history for this stage
    // This is the SECONDARY source of truth for optional stage visibility
    if (followUpHistory && followUpHistory.length > 0) {
      for (const activity of followUpHistory) {
        const channel = (activity.channel || '').toLowerCase();

        if (stageKey === 'storeVisit') {
          if (activity.type === 'storeVisit' || channel.includes('store visit')) {
            return true;
          }
        } else if (stageKey === 'siteVisit') {
          if (activity.type === 'siteVisit' || channel.includes('site visit')) {
            return true;
          }
        } else if (stageKey === 'designFinalized') {
          if (channel.includes('design') && (channel.includes('finalized') || channel.includes('final'))) {
            return true;
          }
        }
      }
    }

    // DO NOT use stage order to determine visibility for optional stages
    // Optional stages can be skipped, so being at measurement/quotation
    // does NOT mean storeVisit was visited if there's no evidence in 
    // stageSelectedAt or followUpHistory

    return false;
  }

  return true; // Default to visible
};

/**
 * Get stage status based on current stage and sub-status
 */
export const getStageStatus = (
  stageKey: DetailedStage,
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string
): StageStatus => {
  // If this is the current stage
  if (currentDetailedStage === stageKey) {
    // Check if it's completed based on sub-status
    if (!currentStageSubStatus ||
      currentStageSubStatus === 'completed' ||
      currentStageSubStatus === 'approved' ||
      stageKey === 'designFinalized') {
      return 'completed';
    }
    return 'pending';
  }

  // If current stage is beyond this stage, this stage is completed
  const stageOrder: DetailedStage[] = [
    'new', 'contacted', 'followup', 'storeVisit', 'designFinalized',
    'siteVisit', 'measurement', 'quotation', 'won', 'lost', 'hold'
  ];

  const currentIndex = currentDetailedStage ? stageOrder.indexOf(currentDetailedStage) : -1;
  const stageIndex = stageOrder.indexOf(stageKey);

  if (currentIndex > stageIndex) {
    return 'completed';
  }

  return 'pending';
};

/**
 * Get all visible stages with their status
 */
export const getVisibleStages = (
  followUpHistory?: Array<{
    type?: 'phone' | 'whatsapp' | 'email' | 'siteVisit' | 'storeVisit' | 'followup';
    channel?: string;
    date: string;
  }>,
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string
): Stage[] => {
  return DETAILED_STAGES
    .map(stageConfig => {
      const visible = getStageVisibility(
        stageConfig.key,
        followUpHistory,
        currentDetailedStage
      );

      if (!visible) {
        return null;
      }

      const status = getStageStatus(
        stageConfig.key,
        currentDetailedStage,
        currentStageSubStatus
      );

      return {
        key: stageConfig.key,
        label: stageConfig.label,
        visible: true,
        status,
      };
    })
    .filter((stage): stage is Stage => stage !== null);
};

/**
 * Get available next actions based on completed stages
 */
export const getAvailableActions = (
  completedStages: DetailedStage[]
): NextAction[] => {
  const availableActions: NextAction[] = [];

  for (const [action, rule] of Object.entries(ACTION_RULES)) {
    const actionKey = action as NextAction;

    if (rule.requiresAll) {
      // All stages in allowedAfter must be completed
      const allCompleted = rule.allowedAfter.every(stage =>
        completedStages.includes(stage)
      );
      if (allCompleted) {
        availableActions.push(actionKey);
      }
    } else {
      // At least one stage in allowedAfter must be completed
      const anyCompleted = rule.allowedAfter.some(stage =>
        completedStages.includes(stage)
      );
      if (anyCompleted) {
        availableActions.push(actionKey);
      }
    }
  }

  return availableActions;
};

/**
 * Get completed stages from lead data
 */
export const getCompletedStages = (
  currentDetailedStage?: DetailedStage,
  currentStageSubStatus?: string,
  followUpHistory?: Array<{
    type?: 'phone' | 'whatsapp' | 'email' | 'siteVisit' | 'storeVisit' | 'followup';
    channel?: string;
    date: string;
  }>
): DetailedStage[] => {
  const completed: DetailedStage[] = [];

  if (!currentDetailedStage) {
    return completed;
  }

  const stageOrder: DetailedStage[] = [
    'new', 'contacted', 'followup', 'storeVisit', 'designFinalized',
    'siteVisit', 'measurement', 'quotation', 'won', 'lost', 'hold'
  ];

  const currentIndex = stageOrder.indexOf(currentDetailedStage);

  // Add all stages before current as completed
  for (let i = 0; i < currentIndex; i++) {
    completed.push(stageOrder[i]);
  }

  // Check if current stage is completed
  const isCurrentCompleted =
    !currentStageSubStatus ||
    currentStageSubStatus === 'completed' ||
    currentStageSubStatus === 'approved' ||
    currentDetailedStage === 'designFinalized';

  if (isCurrentCompleted) {
    completed.push(currentDetailedStage);
  }

  // Check for optional stages that might be completed
  if (followUpHistory && followUpHistory.length > 0) {
    for (const activity of followUpHistory) {
      const channel = (activity.channel || '').toLowerCase();

      // Check for storeVisit completion
      if (activity.type === 'storeVisit' && channel.includes('completed')) {
        if (!completed.includes('storeVisit')) {
          completed.push('storeVisit');
        }
      }

      // Check for siteVisit completion
      if (activity.type === 'siteVisit' && channel.includes('completed')) {
        if (!completed.includes('siteVisit')) {
          completed.push('siteVisit');
        }
      }

      // Check for designFinalized
      if (channel.includes('design') && (channel.includes('finalized') || channel.includes('final'))) {
        if (!completed.includes('designFinalized')) {
          completed.push('designFinalized');
        }
      }
    }
  }

  return completed;
};

/**
 * Check if a stage is terminal (won/lost/hold)
 */
export const isTerminalStage = (stageKey: DetailedStage): boolean => {
  const config = STAGE_CONFIG.find(s => s.key === stageKey);
  return config?.terminal === true;
};

/**
 * Complete a stage - handles dependencies and auto-completion
 * Returns the stages that should be marked as completed
 */
export const completeStage = (
  stageKey: DetailedStage,
  currentStages: DetailedStage[]
): DetailedStage[] => {
  const stagesToComplete: DetailedStage[] = [];

  // Get dependencies for this stage
  const dependencies = DEPENDENCY_MAP[stageKey] || [];

  // Auto-complete dependencies
  dependencies.forEach(dep => {
    if (!currentStages.includes(dep) && !stagesToComplete.includes(dep)) {
      stagesToComplete.push(dep);
    }
  });

  // Add the current stage
  if (!stagesToComplete.includes(stageKey)) {
    stagesToComplete.push(stageKey);
  }

  return stagesToComplete;
};

